import React, { ChangeEvent, ReactNode } from "react";
import {
  Grid,
  TextField,
  Stack,
  Button,
  Paper,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  COMPANY_DETAILS,
  PAGE_MARGIN,
  buildDate,
  buildDateTime,
  buildInvoiceNumber,
  ensureSpace,
  ensureUuid,
  formatCurrency,
  formatDate,
  isValidDateValue,
  sanitizeFileName,
} from "./common";
import { PlusJakartaSansFontBase64String } from "../../../Helpers/PlusJakartaSans-base64String";
import { faviconPngBase64 } from "../../../Helpers/faviconPngBase64";

export interface InternalInvoiceBase {
  documentUuid: string;
  documentDateTime: string;
  invoiceDate: string;
  invoiceNumber: string;
  billToName: string;
  billToAddress: string;
  billToGstin: string;
  billToPan: string;
  placeOfSupply: string;
  hsnSac: string;
  description: string;
  taxableValue: string;
}

export interface InternalCalculations {
  taxableValue: number;
  taxOneLabel: string;
  taxTwoLabel?: string;
  taxOneRate: number;
  taxTwoRate?: number;
  taxOneAmount: number;
  taxTwoAmount?: number;
  totalAmount: number;
  amountInWords: string;
}

export const createInternalBaseForm = (): InternalInvoiceBase => ({
  documentUuid: ensureUuid(),
  documentDateTime: buildDateTime(),
  invoiceDate: buildDate(),
  invoiceNumber: buildInvoiceNumber(),
  billToName: "",
  billToAddress: "",
  billToGstin: "",
  billToPan: "",
  placeOfSupply: "Maharashtra",
  hsnSac: "997152",
  description: "",
  taxableValue: "",
});

export const validateInternalBaseFields = (form: InternalInvoiceBase) => {
  const validation: Record<string, string> = {};

  if (!form.documentUuid.trim()) validation.documentUuid = "Required";
  if (!isValidDateValue(form.documentDateTime))
    validation.documentDateTime = "Invalid date";
  if (!isValidDateValue(form.invoiceDate))
    validation.invoiceDate = "Invalid date";
  if (!form.invoiceNumber.trim()) validation.invoiceNumber = "Required";
  if (!form.billToName.trim()) validation.billToName = "Required";
  if (!form.billToAddress.trim()) validation.billToAddress = "Required";
  if (!form.billToGstin.trim()) validation.billToGstin = "Required";
  if (!form.billToPan.trim()) validation.billToPan = "Required";
  if (!form.placeOfSupply.trim()) validation.placeOfSupply = "Required";
  if (!form.hsnSac.trim()) validation.hsnSac = "Required";
  if (!form.description.trim()) validation.description = "Required";
  if (!form.taxableValue.trim())
    validation.taxableValue = "Enter a valid amount";

  return validation;
};

interface InternalInvoiceBaseFieldsProps {
  form: InternalInvoiceBase;
  errors: Record<string, string>;
  onBaseChange: (
    field: keyof InternalInvoiceBase
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onRegenerateUuid: () => void;
  onApplyInvoiceNumber: () => void;
  calculations: InternalCalculations;
  children?: ReactNode;
}

export const InternalInvoiceBaseFields: React.FC<
  InternalInvoiceBaseFieldsProps
> = ({
  form,
  errors,
  onBaseChange,
  onRegenerateUuid,
  onApplyInvoiceNumber,
  calculations,
  children,
}) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Stack direction="row" spacing={1}>
        <TextField
          value={form.documentUuid}
          onChange={onBaseChange("documentUuid")}
          label="Document UUID"
          fullWidth
          error={Boolean(errors.documentUuid)}
          helperText={errors.documentUuid}
        />
        <Button variant="outlined" onClick={onRegenerateUuid}>
          Regenerate
        </Button>
      </Stack>
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Document Date & Time"
        type="datetime-local"
        value={form.documentDateTime}
        onChange={onBaseChange("documentDateTime")}
        fullWidth
        InputLabelProps={{ shrink: true }}
        error={Boolean(errors.documentDateTime)}
        helperText={errors.documentDateTime}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Invoice Date"
        type="date"
        value={form.invoiceDate}
        onChange={onBaseChange("invoiceDate")}
        fullWidth
        InputLabelProps={{ shrink: true }}
        error={Boolean(errors.invoiceDate)}
        helperText={errors.invoiceDate}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <Stack direction="row" spacing={1}>
        <TextField
          value={form.invoiceNumber}
          onChange={onBaseChange("invoiceNumber")}
          label="Invoice Number"
          fullWidth
          error={Boolean(errors.invoiceNumber)}
          helperText={errors.invoiceNumber}
        />
        <Button variant="outlined" onClick={onApplyInvoiceNumber}>
          Apply Default
        </Button>
      </Stack>
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Bill To - Name"
        value={form.billToName}
        onChange={onBaseChange("billToName")}
        fullWidth
        error={Boolean(errors.billToName)}
        helperText={errors.billToName}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Bill To - GSTIN"
        value={form.billToGstin}
        onChange={onBaseChange("billToGstin")}
        fullWidth
        error={Boolean(errors.billToGstin)}
        helperText={errors.billToGstin}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Bill To - Address"
        value={form.billToAddress}
        onChange={onBaseChange("billToAddress")}
        fullWidth
        multiline
        minRows={3}
        error={Boolean(errors.billToAddress)}
        helperText={errors.billToAddress}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Bill To - PAN"
        value={form.billToPan}
        onChange={onBaseChange("billToPan")}
        fullWidth
        error={Boolean(errors.billToPan)}
        helperText={errors.billToPan}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Place of Supply"
        value={form.placeOfSupply}
        onChange={onBaseChange("placeOfSupply")}
        fullWidth
        error={Boolean(errors.placeOfSupply)}
        helperText={errors.placeOfSupply}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="HSN/SAC"
        value={form.hsnSac}
        onChange={onBaseChange("hsnSac")}
        fullWidth
        error={Boolean(errors.hsnSac)}
        helperText={errors.hsnSac}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Description"
        value={form.description}
        onChange={onBaseChange("description")}
        fullWidth
        multiline
        minRows={3}
        error={Boolean(errors.description)}
        helperText={errors.description}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Taxable Value (INR)"
        value={form.taxableValue}
        onChange={onBaseChange("taxableValue")}
        fullWidth
        error={Boolean(errors.taxableValue)}
        helperText={errors.taxableValue}
        type="number"
        inputProps={{ min: 0, step: "0.01" }}
      />
    </Grid>
    {children}
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.default" }}>
        <Typography variant="subtitle2">Invoice Preview</Typography>
        <Typography variant="body2">
          Taxable Value:{" "}
          {Number.isFinite(calculations.taxableValue)
            ? formatCurrency(calculations.taxableValue)
            : "--"}
        </Typography>
        <Typography variant="body2">
          {calculations.taxOneLabel}:{" "}
          {Number.isFinite(calculations.taxOneAmount)
            ? formatCurrency(calculations.taxOneAmount)
            : "--"}
        </Typography>
        {calculations.taxTwoLabel && (
          <Typography variant="body2">
            {calculations.taxTwoLabel}:{" "}
            {Number.isFinite(calculations.taxTwoAmount ?? Number.NaN)
              ? formatCurrency(calculations.taxTwoAmount ?? 0)
              : "--"}
          </Typography>
        )}
        <Typography variant="body2">
          Total:{" "}
          {Number.isFinite(calculations.totalAmount)
            ? formatCurrency(calculations.totalAmount)
            : "--"}
        </Typography>
        <Typography variant="body2">
          Amount in Words: {calculations.amountInWords || "--"}
        </Typography>
      </Paper>
    </Grid>
  </Grid>
);

export const generateInternalInvoicePdf = (
  form: InternalInvoiceBase,
  calculations: InternalCalculations,
  head: string[][],
  body: string[][],
  fileSuffix: string
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const primaryText = { r: 35, g: 38, b: 45 };
  const mutedText = { r: 107, g: 114, b: 128 };
  const headingText = { r: 15, g: 23, b: 42 };

  doc.addFileToVFS(
    "PlusJakartaSans-Regular.ttf",
    PlusJakartaSansFontBase64String
  );
  doc.addFont("PlusJakartaSans-Regular.ttf", "PlusJakarta", "normal");
  doc.setFont("PlusJakarta", "normal");

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  let cursorY = PAGE_MARGIN;
  const logoSize = 18;
  doc.addImage(
    `data:image/png;base64,${faviconPngBase64}`,
    "PNG",
    PAGE_MARGIN,
    cursorY - 2,
    logoSize,
    logoSize
  );

  doc.setFontSize(13);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(COMPANY_DETAILS.name, PAGE_MARGIN + logoSize + 6, cursorY + 3);

  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("ORIGINAL FOR RECIPIENT", pageWidth - PAGE_MARGIN, cursorY, {
    align: "right",
  });

  cursorY += 5;
  const companyLines = [
    ...COMPANY_DETAILS.addressLines,
    `GSTIN: ${COMPANY_DETAILS.gstin}`,
    `PAN: ${COMPANY_DETAILS.pan}`,
    `CIN: ${COMPANY_DETAILS.cin}`,
  ];
  const infoStartX = PAGE_MARGIN + logoSize + 6;
  doc.setFontSize(9);
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  companyLines.forEach((line) => {
    doc.text(line, infoStartX, cursorY + 4);
    cursorY += 4;
  });
  cursorY += 6;

  doc.setFontSize(16);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("TAX INVOICE", pageWidth / 2, cursorY, { align: "center" });
  cursorY += 7;
  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("e-Invoice Details", pageWidth / 2, cursorY, { align: "center" });
  cursorY += 8;

  doc.setFontSize(9);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("Document UUID", PAGE_MARGIN, cursorY);
  doc.text("Document Date", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 5;
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(form.documentUuid, PAGE_MARGIN, cursorY);
  doc.text(
    formatDate(form.documentDateTime, "DD/MM/YYYY HH:mm"),
    pageWidth - PAGE_MARGIN - 60,
    cursorY
  );
  cursorY += 8;

  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(
    "Billing (Bill To) and Delivery Address (Ship To)",
    PAGE_MARGIN,
    cursorY
  );
  doc.text("Invoice Information", pageWidth - PAGE_MARGIN - 60, cursorY);
  cursorY += 6;

  const billLines = [
    form.billToName,
    ...form.billToAddress
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean),
    `GSTIN: ${form.billToGstin}`,
    `PAN: ${form.billToPan}`,
  ].filter(Boolean);
  let billY = cursorY;
  doc.setFontSize(9.5);
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(billLines[0] || "", PAGE_MARGIN, billY);
  billY += 5;
  doc.setFontSize(9);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  billLines.slice(1).forEach((line) => {
    doc.text(line, PAGE_MARGIN, billY);
    billY += 4.2;
  });

  const infoLabelX = pageWidth - PAGE_MARGIN - 60;
  const infoValueX = pageWidth - PAGE_MARGIN;
  let infoY = cursorY;
  const invoiceInfo: Array<[string, string]> = [
    ["Date", formatDate(form.invoiceDate, "DD/MM/YYYY")],
    ["Invoice Number", form.invoiceNumber],
    ["Place of Supply", form.placeOfSupply],
  ];
  invoiceInfo.forEach(([label, value]) => {
    doc.setFontSize(9);
    doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
    doc.text(`${label}:`, infoLabelX, infoY);
    doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
    doc.text(value, infoValueX, infoY, { align: "right" });
    infoY += 4.8;
  });

  cursorY = Math.max(billY, infoY) + 8;

  autoTable(doc, {
    startY: cursorY,
    head,
    body,
    theme: "grid",
    styles: {
      font: "PlusJakarta",
      fontSize: 8.5,
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      textColor: [primaryText.r, primaryText.g, primaryText.b],
    },
    headStyles: {
      font: "PlusJakarta",
      fontSize: 8.5,
      textColor: 255,
      fillColor: [35, 38, 45],
      fontStyle: "bold",
    },
    bodyStyles: {
      textColor: [primaryText.r, primaryText.g, primaryText.b],
    },
    columnStyles: head[0].reduce((acc, _, index) => {
      acc[index] = {
        halign: index === 0 ? "left" : index === 1 ? "center" : "right",
      };
      return acc;
    }, {} as Record<number, { halign: "left" | "center" | "right" }>),
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });

  cursorY = (doc as any).lastAutoTable.finalY + 10;

  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  const wordsLabel = "Total amount in words:";
  const wordsLabelWidth = doc.getTextWidth(wordsLabel);
  doc.text(wordsLabel, PAGE_MARGIN, cursorY);
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text(
    calculations.amountInWords || "--",
    PAGE_MARGIN + wordsLabelWidth + 2,
    cursorY
  );
  cursorY += 10;

  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text("Terms and Conditions:", PAGE_MARGIN, cursorY);
  cursorY += 5;
  doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
  doc.text("Payment Terms: Immediate", PAGE_MARGIN, cursorY);
  cursorY += 10;

  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(`For ${COMPANY_DETAILS.name}`, PAGE_MARGIN, cursorY);
  cursorY += 16;
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  doc.text("Authorized Signatory", PAGE_MARGIN, cursorY);
  cursorY += 10;

  doc.setFontSize(9.5);
  doc.setTextColor(headingText.r, headingText.g, headingText.b);
  doc.text(
    "Bank Information for Direct Deposit and Wire Transfer",
    PAGE_MARGIN,
    cursorY
  );
  cursorY += 7;

  const bankEntries: Array<[string, string]> = [
    ["Beneficiary Name", COMPANY_DETAILS.bank.beneficiary],
    ["Bank Name and Branch", COMPANY_DETAILS.bank.bankName],
    ["Account Number", COMPANY_DETAILS.bank.accountNumber],
    ["IFSC Code", COMPANY_DETAILS.bank.ifsc],
  ];
  const bankLabelWidth = 60;
  bankEntries.forEach(([label, value]) => {
    cursorY = ensureSpace(doc, cursorY, 8);
    doc.setFontSize(9);
    doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
    doc.text(label, PAGE_MARGIN, cursorY);
    doc.setTextColor(primaryText.r, primaryText.g, primaryText.b);
    doc.text(value || "--", PAGE_MARGIN + bankLabelWidth, cursorY);
    cursorY += 6;
  });

  const disclaimers = [
    "# Discrepancy, if any, noticed in the above Invoice shall be informed to us within seven days of the receipt of this Invoice. In case no communication is received within fifteen days, then the same will be treated as accepted by the client.",
  ];
  const lineHeight = 3.6;
  const wrappedDisclaimers = disclaimers.map((line) =>
    doc.splitTextToSize(line, pageWidth - PAGE_MARGIN * 2)
  );
  const disclaimersHeight =
    wrappedDisclaimers.reduce((sum, lines) => sum + lines.length, 0) *
      lineHeight +
    (wrappedDisclaimers.length - 1) * 1.5;
  let disclaimersY = pageHeight - PAGE_MARGIN - disclaimersHeight;
  if (disclaimersY < cursorY + 6) {
    disclaimersY = cursorY + 6;
  }
  doc.setFontSize(7.5);
  doc.setTextColor(mutedText.r, mutedText.g, mutedText.b);
  wrappedDisclaimers.forEach((lines, index) => {
    doc.text(lines, PAGE_MARGIN, disclaimersY, { lineHeightFactor: 1.25 });
    disclaimersY += lines.length * lineHeight + (index === 0 ? 1 : 0.5);
  });

  const fileName = `Internal_${fileSuffix}_Invoice_${sanitizeFileName(
    form.invoiceNumber,
    "invoice"
  )}.pdf`;
  doc.save(fileName);
};
